/**
 * What the symbols mean, in the reader's language — drawn, not described.
 *
 * On the board rather than in documentation because the notation is only worth
 * using if both people in the room can read it, and the patient meets it for
 * the first time in the session where it is being drawn about them. A word
 * alone does not teach a symbol: "gemelos" and "mellizos" are one bar apart,
 * and the bar is the whole difference.
 *
 * Every swatch is the board's own glyph component, given a small viewBox — not
 * a drawing of it. A legend that redraws the notation is a legend that can
 * disagree with the board, and the one place a reader must be able to trust is
 * the key.
 */

import { useTranslation } from "react-i18next";

import {
    CHILD_TYPES,
    EMOTIONAL_TYPES,
    ENERGY_DIRECTIONS,
    LINK_STRENGTHS,
    PERSON_GENDERS,
    UNION_TYPES,
    type ChildType,
    type EmotionalType,
    type EnergyDirection,
    type LinkStrength,
    type MapKind,
    type PersonGender,
    type PersonNode,
    type SystemNode,
    type UnionType,
} from "../../domain/types";
import {
    ArrowMarkers,
    CentreShape,
    DeceasedCross,
    EmotionalLine,
    HouseholdBoundary,
    IndexRing,
    PersonShape,
    Sibship,
    SystemShape,
    SystemTie,
    UnionLine,
} from "./MapGlyphs";

/** A person with nothing filled in, so a swatch shows the shape and no more. */
const swatchPerson = (over: Partial<PersonNode> = {}): PersonNode => ({
    id: "legend",
    type: "person",
    gender: "male",
    name: "",
    birthYear: null,
    deathYear: null,
    deceased: false,
    index: false,
    notes: "",
    x: 0,
    y: 0,
    ...over,
});

const swatchSystem = (over: Partial<SystemNode> = {}): SystemNode => ({
    id: "legend",
    type: "system",
    system: "work",
    label: "",
    x: 0,
    y: 0,
    ...over,
});

/**
 * One symbol, boxed.
 *
 * The box is a fixed size and the viewBox is whatever bounds the drawing:
 * `preserveAspectRatio` then fits it without distorting it, so a tall symbol
 * (a child's line) and a wide one (a union) can sit in the same column.
 */
function Swatch({ viewBox, children }: { viewBox: string; children: React.ReactNode }) {
    return (
        <svg
            viewBox={viewBox}
            width={56}
            height={38}
            aria-hidden="true"
            focusable="false"
            className="shrink-0 text-content-secondary"
        >
            {children}
        </svg>
    );
}

/** One row: the symbol, and what it is called. */
function Row({ swatch, label }: { swatch: React.ReactNode; label: string }) {
    return (
        <li className="flex items-center gap-2">
            {swatch}
            <span className="text-xs leading-4 text-content-secondary">{label}</span>
        </li>
    );
}

const PersonSwatch = ({ person }: { person: PersonNode }) => (
    <Swatch viewBox="-32 -32 64 64">
        <PersonShape person={person} />
        <IndexRing person={person} />
        <DeceasedCross person={person} />
    </Swatch>
);

/**
 * The couple's line, read across its horizontal run.
 *
 * The run is where the notation lives — the slashes of a separation, the two
 * of a divorce — so the viewBox is a band around it rather than the whole
 * drop from each partner.
 */
const UnionSwatch = ({ union }: { union: UnionType }) => (
    <Swatch viewBox="-2 66 64 24">
        <UnionLine a={{ x: 4, y: 0 }} b={{ x: 56, y: 0 }} union={union} />
    </Swatch>
);

/**
 * A child's own line down from the sibship.
 *
 * Twins are drawn as the pair they are: one apex cannot be shown with one
 * child, and the apex is the symbol.
 */
const ChildSwatch = ({ child }: { child: ChildType }) => {
    const twins = child === "twin" || child === "identicalTwin";
    // A miscarriage is read on the figure, not on the line, so the swatch has
    // to show the figure.
    const lost = child === "miscarriage";
    const members = (twins ? [12, 48] : [30]).map((x, index) => ({
        id: `c${index}`,
        kind: child,
        at: { x, y: 112 },
        selected: false,
        label: "",
        onSelect: () => {},
    }));

    return (
        <Swatch viewBox={twins ? "2 64 56 60" : lost ? "4 64 52 76" : "16 64 28 60"}>
            <Sibship anchor={{ x: 30, y: 0 }} members={members} />
            {lost && (
                <g transform="translate(30 112)">
                    <PersonShape person={swatchPerson({ gender: "unknown" })} filled />
                </g>
            )}
        </Swatch>
    );
};

const EmotionalSwatch = ({ emotional }: { emotional: EmotionalType }) => (
    <Swatch viewBox="0 0 60 22">
        <EmotionalLine a={{ x: 4, y: 11 }} b={{ x: 56, y: 11 }} kind={emotional} />
    </Swatch>
);

/**
 * An ecomap tie.
 *
 * Strength and direction are separate qualities of one line, so each swatch
 * shows the one it is naming and holds the other steady.
 */
const TieSwatch = ({
    id,
    strength,
    energy,
}: {
    id: string;
    strength: LinkStrength;
    energy: EnergyDirection;
}) => (
    <Swatch viewBox="0 0 60 22">
        <ArrowMarkers idPrefix={`legend-${id}`} />
        <SystemTie
            a={{ x: 6, y: 11 }}
            b={{ x: 54, y: 11 }}
            strength={strength}
            // The swatch is drawn centre-on-the-left, the way the board draws
            // a tie from the centre outward.
            arrowAtA={energy !== "towardSystem"}
            arrowAtB={energy !== "towardPerson"}
            markerStart={`legend-${id}-start`}
            markerEnd={`legend-${id}-end`}
        />
    </Swatch>
);

export default function MapLegend({ kind }: { kind: MapKind }) {
    const { t } = useTranslation();

    const genogramSections = [
        {
            title: t("systemicMap.legend.people"),
            rows: [
                ...PERSON_GENDERS.map((gender: PersonGender) => ({
                    key: gender,
                    label: t(`systemicMap.gender.${gender}`),
                    swatch: <PersonSwatch person={swatchPerson({ gender })} />,
                })),
                {
                    key: "deceased",
                    label: t("systemicMap.person.deceased"),
                    swatch: <PersonSwatch person={swatchPerson({ deceased: true })} />,
                },
                {
                    key: "index",
                    label: t("systemicMap.person.indexPatient"),
                    swatch: <PersonSwatch person={swatchPerson({ index: true })} />,
                },
                {
                    key: "household",
                    label: t("systemicMap.palette.household"),
                    swatch: (
                        <Swatch viewBox="-58 -46 116 92">
                            <HouseholdBoundary
                                points={[
                                    { x: -14, y: 0 },
                                    { x: 14, y: 0 },
                                ]}
                                label=""
                            />
                            {/* Two people inside it: the boundary means "these
                                live together", which an empty box does not. */}
                            <g transform="translate(-14 0)">
                                <PersonShape person={swatchPerson()} />
                            </g>
                            <g transform="translate(14 0)">
                                <PersonShape person={swatchPerson({ gender: "female" })} />
                            </g>
                        </Swatch>
                    ),
                },
            ],
        },
        {
            title: t("systemicMap.legend.unions"),
            rows: UNION_TYPES.map((union) => ({
                key: union,
                label: t(`systemicMap.union.${union}`),
                swatch: <UnionSwatch union={union} />,
            })),
        },
        {
            title: t("systemicMap.legend.children"),
            rows: CHILD_TYPES.map((child) => ({
                key: child,
                label: t(`systemicMap.child.${child}`),
                swatch: <ChildSwatch child={child} />,
            })),
        },
        {
            title: t("systemicMap.legend.emotional"),
            rows: EMOTIONAL_TYPES.map((emotional) => ({
                key: emotional,
                label: t(`systemicMap.emotional.${emotional}`),
                swatch: <EmotionalSwatch emotional={emotional} />,
            })),
        },
    ];

    const ecomapSections = [
        {
            title: t("systemicMap.legend.places"),
            rows: [
                {
                    key: "centre",
                    label: t("systemicMap.canvas.centre"),
                    swatch: (
                        <Swatch viewBox="-34 -34 68 68">
                            <CentreShape />
                        </Swatch>
                    ),
                },
                {
                    key: "system",
                    label: t("systemicMap.palette.addSystem"),
                    swatch: (
                        <Swatch viewBox="-28 -28 56 56">
                            <SystemShape system={swatchSystem()} />
                        </Swatch>
                    ),
                },
                {
                    key: "custom",
                    label: t("systemicMap.system.custom"),
                    swatch: (
                        <Swatch viewBox="-28 -28 56 56">
                            <SystemShape system={swatchSystem({ system: "custom" })} />
                        </Swatch>
                    ),
                },
            ],
        },
        {
            title: t("systemicMap.legend.strength"),
            rows: LINK_STRENGTHS.map((strength) => ({
                key: strength,
                label: t(`systemicMap.strength.${strength}`),
                swatch: (
                    <TieSwatch
                        id={`s-${strength}`}
                        strength={strength}
                        energy="towardSystem"
                    />
                ),
            })),
        },
        {
            title: t("systemicMap.legend.energy"),
            rows: ENERGY_DIRECTIONS.map((energy) => ({
                key: energy,
                label: t(`systemicMap.energy.${energy}`),
                swatch: <TieSwatch id={`e-${energy}`} strength="strong" energy={energy} />,
            })),
        },
    ];

    // An ecomap holds both vocabularies: the star of ties around the centre,
    // and the household drawn inside it. The key has to teach both.
    const sections =
        kind === "genogram"
            ? genogramSections
            : [...ecomapSections, ...genogramSections];

    return (
        <section aria-labelledby="systemic-map-legend" className="space-y-4">
            <h3
                id="systemic-map-legend"
                className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary"
            >
                {t("systemicMap.legend.title")}
            </h3>
            {sections.map((section) => (
                <div key={section.title}>
                    <h4 className="mb-1 text-[13px] font-semibold text-content-primary">
                        {section.title}
                    </h4>
                    <ul className="space-y-0.5">
                        {section.rows.map((row) => (
                            <Row key={row.key} swatch={row.swatch} label={row.label} />
                        ))}
                    </ul>
                </div>
            ))}
            <p className="text-xs leading-4 text-content-tertiary">
                {t("systemicMap.legend.source")}
            </p>
        </section>
    );
}
